<template lang="pug">
AppTabs(
  hide-nav-tabs
  :selected-tab="selectedTab"
)
  .row
    .col-xs-6.total-container(:class="{ 'col-sm-4': hasFilters }")
      .row(v-if="!daily")
        .col-xs-12
          h4.d-inline-flex.align-items-center.gap-5
            .date
              | Total:
            .font-thin
              | {{ totalDuration | formatDuration }}
            TimeEntriesOvertimePopover(
              v-if="organization"
              :time-entries="timeEntries"
              :organization="organization"
              :show-financial="isOwner"
            )
            TimeEntriesCopyPreviewPopover(
              v-if="weekly && showWeeklyCopyButton && !showLoading"
              :disabled="showCopyGrid"
            )
              template(slot="trigger")
                a.time-entries-copy-btn.btn.btn-icon.btn-naked.has-icon(
                  :disabled="showCopyGrid"
                  @click="toggleWeeklyCopy"
                )
                  i.hi.hi-clipboard.hi-16
      AppDropdown.pull-left.hidden-xs(
        v-if="daily && !timeEntriesToBatchDeleteIsEmpty"
        direction="left"
        label="Batch actions"
      )
        AppDropdownItem(
          v-tooltip
          :title="deleteMultipleTimeEntriesTooltip"
          :disabled="preventOwnTimeDeletion"
          @click="showNewTimeEntriesDeleteMultipleDialog"
        ) Delete
    .col-xs-6.col-sm-4.p-0.text-center(v-if="hasFilters")
      AppFilterCount(
        :count="filterCount"
        @click="doShowFilters"
        @clear="clearFilters"
      )
    .pl-0(
      :class="{ 'col-xs-6': !hasFilters && !daily, 'col-xs-12 col-sm-4': hasFilters, 'col-sm-6 col-xs-12': !hasFilters && daily }"
    )
      .page-header-actions.pull-right
        TimeEntriesColumnPicker(
          v-if="daily"
          :columns="tableColumns"
          @change="updateTableColumns"
        )
        AppExportMenu(
          v-if="!removedUser"
          :disabled="timeEntriesIsEmpty"
          hide-caret
          type-classes="btn btn-icon btn-naked"
          icon-classes="hi hi-export hi-16"
          text=""
        )
          AppExportMenuItem
            AppLink(
              format="pdf"
              :url-query="exportParams"
              :url="exportUrlForTab"
            ) To PDF
          AppExportMenuItem
            AppLink(
              format="csv"
              :url-query="exportParams"
              :url="exportUrlForTab"
            ) To CSV

        AppButton.add-time-btn.ml-10(
          v-if="showAddEntry"
          v-tooltip="{ placement: 'bottom' }"
          type="primary"
          :disabled="isAddTimeButtonDisabled"
          :data-original-title="addTimeDisabled"
          @click="showTimeEntriesFormDialog"
        ) Add time
        slot(name="buttons")
        TimeEntriesFormDialog(
          v-if="!firstLoad"
          :show="timeEntriesFormDialog.show"
          :type="timeEntriesFormDialog.type"
          :time-entry="selectedTimeEntry"
          @close="closeDialog('timeEntriesFormDialog')"
        )
        TimeEntriesSplitDialog(
          :show="timeEntriesSplitDialog.show"
          :time-entry="selectedTimeEntry"
          @close="closeDialog('timeEntriesSplitDialog')"
        )
        TimeEntriesDeleteDialog(
          :show="timeEntriesDeleteDialog.show"
          :time-entry="selectedTimeEntry"
          @close="closeDialog('timeEntriesDeleteDialog')"
        )
        TimeEntriesDeleteMultipleDialog(
          :time-entries="timeEntriesToBatchDelete"
          :show="showTimeEntriesDeleteMultipleDialog"
          @close="closeTimeEntriesDeleteMultipleDialog"
        )
  TimeOffRequestsAndHolidaysList(
    v-if="!calendar"
    :time-off-requests="timeOffRequests"
    :holidays="holidays"
    :zone="currentZone.name"
    :timesheet-team-payment-id="timesheet?.team_payment_id"
  )
  AppTab(
    label="Daily"
    :selected="daily"
  )
    .time-entries(v-if="daily")
      AppInfiniteScroll.infinite-scroll.w-full(
        max-height="auto"
        :show-more-link="hasNextPage"
        @loadmore="loadMoreData"
      )
        TimeEntriesDaily(
          v-for="group in dateRanges"
          :key="group.date"
          :time-entries="timeEntries"
          :date-end="group.date_end"
          :date="group.date"
          :is-owner="isOwner"
          :order="order"
          :show-timeline="showTimeline"
          :show-empty="showEmptyEntries"
        )
        TimeEntriesDailyLoading(v-if="showLoading")
        slot(name="dailyEntries")
  AppTab(
    label="Weekly"
    :selected="weekly"
  )
    .time-entries(v-if="weekly")
      TimeEntriesWeeklyLoading(v-if="showLoading")
      TimeEntriesWeekly(
        v-else
        :time-entries="timeEntries"
        :show-copy-grid="showCopyGrid"
        :is-owner="isOwner"
      )
  AppTab(
    label="Calendar"
    :selected="calendar"
  )
    .time-entries(v-if="calendar")
      TimeEntriesCalendarLoading(v-if="showLoading")
      TimeEntriesCalendar(
        v-else
        :time-entries="timeEntries"
      )
</template>

<script>
import { isEmpty } from 'lodash-es';
import { mapActions, mapGetters, mapState } from 'vuex';
import { createWithZone, formatDatetime } from '~/utils/time_utils';
import AppInfiniteScroll from '~/components/AppInfiniteScroll.vue';

export default {
  name: 'TimeEntriesList',
  components: {
    AppInfiniteScroll,
  },
  props: {
    showScreenshotsColumn: {
      type: Boolean,
      default: true,
    },
    showAddEntry: {
      type: Boolean,
      default: true,
    },
    showTimeline: {
      type: Boolean,
      default: true,
    },
    showEmptyEntries: {
      type: Boolean,
      default: true,
    },
    order: {
      type: String,
      default: 'desc',
    },
    dateRanges: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      showWeeklyCopy: false,
      showFilters: false,
      showTimeEntriesDeleteMultipleDialog: false,
      memberFilter: {},
    };
  },
  computed: {
    ...mapState(['timeEntriesFormDialog', 'timeEntriesSplitDialog', 'timeEntriesDeleteDialog', 'firstLoad']),
    ...mapState({
      isOwner: 'is_owner',
      preventOwnTimeDeletion: 'prevent_own_time_deletion',
      timesheet: 'timesheet',
    }),
    ...mapGetters([
      'ownActivity',
      'showLoading',
      'timeEntries',
      'organization',
      'allowModifyTime',
      'currentUserId',
      'timeOffRequests',
      'holidays',
      'projects',
      'tasks',
      'currentZone',
      'timeEntriesToBatchDelete',
      'timeEntriesQuery',
      'timeEntriesDailyExportUrl',
      'timeEntriesWeeklyExportUrl',
      'newTimeEntry',
      'addTimeDisabled',
      'selectedTimeEntry',
      'sumDuration',
      'skeletonsIsEmpty',
      'lockedRangeFor',
      'view',
      'date',
      'dateEnd',
      'inPlaceTimeEntries',
      'filteredProjectTasks',
      'weeklyProjectTasks',
      'tableColumns',
      'pagination',
      'removedUser',
    ]),
    selectedTab() {
      return this.view ? this.view : 'daily';
    },
    lockedRange() {
      return this.lockedRangeFor(this.startDate, this.stopDate);
    },
    showWeeklyCopyButton() {
      const isLocked = !!this.lockedRange.start && !!this.lockedRange.stop;

      return (
        this.timeEntriesIsEmpty &&
        this.inPlaceTimeEntriesIsEmpty &&
        !this.addTimeDisabled &&
        !this.skeletonsIsEmpty &&
        !isLocked
      );
    },
    inPlaceTimeEntriesIsEmpty() {
      return isEmpty(this.inPlaceTimeEntries);
    },
    showCopyGrid() {
      const filters = this.timeEntriesQuery.filters;

      return filters.showWeeklyCopy === 'true' || this.showWeeklyCopy;
    },
    exportParams() {
      const tableColumns = {};

      if (this.tableColumns.show_activity) {
        tableColumns.show_activity = true;
      }
      if (this.tableColumns.show_idle) {
        tableColumns.show_idle = true;
      }
      const filters = Object.assign({}, this.timeEntriesQuery.filters, tableColumns);
      const params = Object.assign({}, this.timeEntriesQuery, { filters });

      return params;
    },
    timeEntriesIsEmpty() {
      return isEmpty(this.timeEntries);
    },
    timeEntriesToBatchDeleteIsEmpty() {
      return isEmpty(this.timeEntriesToBatchDelete);
    },
    startDate() {
      return createWithZone(this.date, null, this.currentZone.name);
    },
    stopDate() {
      return createWithZone(this.dateEnd, null, this.currentZone.name);
    },
    filterCount() {
      let count = 0;
      const filters = this.timeEntriesQuery.filters;
      const projectIds = filters.project_ids;
      const timeTypes = filters.time_types;
      const clientTypes = filters.client_types;
      const activityLevel = filters.activity_level;

      if (projectIds && projectIds.length > 0) {
        count += projectIds.length;
      }
      if (timeTypes && timeTypes.length > 0) {
        count += timeTypes.length;
      }
      if (clientTypes && clientTypes.length > 0) {
        count += clientTypes.length;
      }
      if (activityLevel) {
        count += 1;
      }

      return count;
    },
    hasFilters() {
      return this.filterCount > 0;
    },
    exportUrlForTab() {
      return this.daily ? this.timeEntriesDailyExportUrl : this.timeEntriesWeeklyExportUrl;
    },
    totalDuration() {
      return this.sumDuration(this.timeEntries.concat(this.inPlaceTimeEntries));
    },
    calendar() {
      return this.selectedTab === 'calendar';
    },
    daily() {
      return this.selectedTab === 'daily';
    },
    weekly() {
      return this.selectedTab === 'weekly';
    },
    isAddTimeButtonDisabled() {
      return (
        !!this.addTimeDisabled || (!this.allowModifyTime && this.timeEntriesQuery.filters.user !== this.currentUserId)
      );
    },
    hasNextPage() {
      return !!this.pagination.next_page;
    },
    deleteMultipleTimeEntriesTooltip() {
      return this.preventOwnTimeDeletion ? 'You do not have permission to delete time' : '';
    },
  },
  methods: {
    ...mapActions(['updateTableColumns']),
    loadMoreData() {
      const nextPage = this.pagination.next_page;

      if (!nextPage || this.showLoading) {
        return;
      }

      this.$store.dispatch('loadData', { page: nextPage });
    },
    showTimeEntriesFormDialog() {
      if (!this.addTimeDisabled) {
        this.$store.dispatch('openDialog', {
          dialog: 'timeEntriesFormDialog',
          type: 'add',
          timeEntry: this.newTimeEntry,
        });
      }
    },
    doShowFilters() {
      this.$emit('show-filters');
    },
    clearFilters() {
      this.updateTableColumns({ ...this.tableColumns, show_source: false });
      this.$store.dispatch('clearFilters');
    },
    toggleWeeklyCopy() {
      let verbiage;

      if (!this.showWeeklyCopy) {
        this.showWeeklyCopy = true;
        this.$store.dispatch('updateFilterParams', { filters: { showWeeklyCopy: this.showWeeklyCopy } });
        if (!isEmpty(this.lockedRange)) {
          const start = this.lockedRange.start;
          const stop = this.lockedRange.stop;

          if (stop) {
            verbiage = `Only time after ${formatDatetime(stop)}`;
          } else {
            verbiage = `Only time before ${formatDatetime(start)}`;
          }
          this.$appMessage(`${verbiage} can be added because of a locked timesheet`);
        }
        if (this.filteredProjectTasks.length < this.weeklyProjectTasks.length) {
          verbiage = this.ownActivity ? 'you have been' : `${this.user.name} has been`;
          this.$appMessage(
            `Some projects and tasks could not be copied because they’ve been archived, completed, or ${verbiage} removed from them`,
          );
        }
      }
    },
    showNewTimeEntriesDeleteMultipleDialog() {
      if (this.preventOwnTimeDeletion) {
        return false;
      }

      this.showTimeEntriesDeleteMultipleDialog = true;
    },
    closeTimeEntriesDeleteMultipleDialog() {
      this.showTimeEntriesDeleteMultipleDialog = false;
    },
    closeDialog(dialog) {
      this.$store.dispatch('closeDialog', { dialog });
    },
  },
};
</script>
